import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";
import xrpLogo from "@/assets/tokens/xrp.png";


import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { installTelemetry, reportCrash } from "@/lib/telemetry";
import { WalletProvider } from "@/lib/wallet/store";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="readout mt-2 text-[11px] text-muted">
          This screen doesn't exist in Cobalt Wallet.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="readout inline-flex items-center justify-center rounded-lg bg-accent px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-accent-ink"
          >
            Back to wallet
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    reportCrash(error, "react_error_boundary");
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This screen didn't load
        </h1>
        <p className="readout mt-2 text-[11px] text-muted">
          No keys were touched. Retry, or reopen the wallet.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="readout inline-flex items-center justify-center rounded-lg bg-accent px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-accent-ink"
          >
            Try again
          </button>
          <a
            href="/"
            className="readout inline-flex items-center justify-center rounded-lg border border-border px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-muted"
          >
            Reopen wallet
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { name: "google-site-verification", content: "T7f1Ri2tsJ0-j_oU39sAkznbTKhbykphMZR6E7Lmh5Q" },
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Cobalt Wallet — Self-custody XRPL wallet" },
      {
        name: "description",
        content:
          "Cobalt Wallet is a passkey-secured, non-custodial XRP Ledger wallet. Keys are generated and encrypted on your device.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Cobalt Wallet" },
      { name: "application-name", content: "Cobalt Wallet" },
      { name: "apple-mobile-web-app-title", content: "Cobalt Wallet" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#0b0d10" },
      { name: "referrer", content: "no-referrer" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=3" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/cobalt-favicon-v2.png?v=3" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico?v=3" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/cobalt-icon-v2-180.png" },
      { rel: "apple-touch-icon", sizes: "152x152", href: "/cobalt-icon-v2-152.png" },
      { rel: "manifest", href: "/site.webmanifest?v=3" },

      { rel: "preload", as: "image", href: xrpLogo, type: "image/png" },

      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Space+Grotesk:wght@400;500;600;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    installTelemetry();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <WalletProvider>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        <Toaster position="top-center" />
      </WalletProvider>
    </QueryClientProvider>
  );
}
